import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, HardHat, Globe, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useDebouncedValue } from "../lib/useDebounce";

const TYPES = ["PRIME", "SUBCONTRACTOR"];
const CERT_OPTIONS = ["8(a)", "SDVOSB", "VOSB", "WOSB", "HUBZone", "SDB", "MBE", "DBE"];

export default function Contractors() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [editing, setEditing] = useState(null);

  const dq = useDebouncedValue(q);
  const seq = useRef(0);
  const load = useCallback(() => {
    const s = ++seq.current;
    api.get("/admin/contractors", { params: { q: dq || undefined, contractor_type: type || undefined } })
      .then((r) => { if (s === seq.current) setItems(r.data); })
      .catch(() => { if (s === seq.current) setItems([]); });
  }, [dq, type]);
  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.company_name} from the directory?`)) return;
    try { await api.delete(`/admin/contractors/${c.id}`); toast.success("Contractor removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-contractors-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">Contractors</h1>
        <button data-testid="add-contractor-button" onClick={() => setEditing({})} className="btn-primary !py-2.5 !px-4 text-[13px]"><Plus size={15} /> Add Contractor</button>
      </div>
      <p className="mt-2 text-[13.5px] text-slate-500">Prime and subcontractor teaming partners for government bids.</p>

      <div className="mt-5 flex flex-wrap gap-3">
        <input data-testid="contractor-search-input" className="pg-input !py-2.5 !w-[280px]" placeholder="Search company, trade, certs..." value={q} onChange={(e) => setQ(e.target.value)} />
        <select data-testid="contractor-type-filter" className="pg-input !py-2.5 !w-[190px]" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All types</option>
          <option value="PRIME">Prime</option>
          <option value="SUBCONTRACTOR">Subcontractor</option>
        </select>
      </div>

      {editing && <ContractorForm contractor={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      <div className="mt-5 bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-left text-[13.5px] min-w-[900px]">
          <thead>
            <tr className="border-b border-slate-200 text-[11.5px] uppercase tracking-wider text-slate-500 font-[Poppins]">
              <th className="px-5 py-3">Company</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Trade / Specialty</th>
              <th className="px-5 py-3">Certifications</th><th className="px-5 py-3">Location</th><th className="px-5 py-3">Contact</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items === null && <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-400"><Loader2 className="animate-spin inline" size={18} /></td></tr>}
            {items && items.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-400" data-testid="contractors-empty-state">No contractors yet. Add teaming partners for your government bids.</td></tr>}
            {items && items.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`contractor-row-${c.id}`}>
                <td className="px-5 py-3.5">
                  <div className="font-[Poppins] font-semibold text-[var(--pg-navy)]">{c.company_name}</div>
                  {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="text-[11.5px] text-[var(--pg-accent)] hover:underline inline-flex items-center gap-1"><Globe size={11} /> Website</a>}
                </td>
                <td className="px-5 py-3.5">
                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${c.contractor_type === "PRIME" ? "bg-[var(--pg-navy)] text-white" : "bg-slate-100 text-slate-600"}`}>
                    {c.contractor_type === "PRIME" ? "PRIME" : "SUB"}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-slate-600">{c.trade || "—"}</td>
                <td className="px-5 py-3.5">
                  <div className="flex flex-wrap gap-1">
                    {(c.certifications || []).length === 0 && <span className="text-slate-400">—</span>}
                    {(c.certifications || []).map((x) => <span key={x} className="px-2 py-0.5 rounded-full text-[10.5px] font-[Poppins] font-semibold bg-blue-50 text-blue-700 border border-blue-100">{x}</span>)}
                  </div>
                </td>
                <td className="px-5 py-3.5 text-slate-500">{c.location || "—"}</td>
                <td className="px-5 py-3.5 text-slate-500">
                  <div>{c.contact_name || "—"}</div>
                  {c.email && <div className="text-[12px]">{c.email}</div>}
                  {c.phone && <div className="text-[12px]">{c.phone}</div>}
                </td>
                <td className="px-5 py-3.5 whitespace-nowrap text-right">
                  <button data-testid={`contractor-edit-${c.id}`} onClick={() => setEditing(c)} title="Edit" className="text-slate-500 hover:text-[var(--pg-navy)] p-1.5"><Pencil size={16} /></button>
                  <button data-testid={`contractor-delete-${c.id}`} onClick={() => remove(c)} title="Delete" className="ml-1 text-slate-500 hover:text-red-600 p-1.5"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ContractorForm({ contractor, onClose, onSaved }) {
  const isEdit = Boolean(contractor.id);
  const [f, setF] = useState({
    company_name: contractor.company_name || "", contact_name: contractor.contact_name || "",
    contractor_type: contractor.contractor_type || "SUBCONTRACTOR", trade: contractor.trade || "",
    certifications: contractor.certifications || [], location: contractor.location || "",
    email: contractor.email || "", phone: contractor.phone || "", website: contractor.website || "",
    notes: contractor.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const h = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const toggleCert = (c) => setF((x) => ({ ...x, certifications: x.certifications.includes(c) ? x.certifications.filter((y) => y !== c) : [...x.certifications, c] }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/admin/contractors/${contractor.id}`, f);
      else await api.post("/admin/contractors", f);
      toast.success(isEdit ? "Contractor updated" : "Contractor added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4 overflow-y-auto py-8" onClick={onClose}>
      <form data-testid="contractor-form" onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-2xl p-8 shadow-2xl">
        <h2 className="font-[Poppins] font-bold text-[19px] text-[var(--pg-navy)] flex items-center gap-2"><HardHat size={19} /> {isEdit ? "Edit Contractor" : "Add Contractor"}</h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className="pg-label">Company Name *</label><input data-testid="contractor-company-input" className="pg-input" value={f.company_name} onChange={h("company_name")} required minLength={2} /></div>
          <div><label className="pg-label">Type</label>
            <select data-testid="contractor-type-input" className="pg-input" value={f.contractor_type} onChange={h("contractor_type")}>
              {TYPES.map((t) => <option key={t} value={t}>{t === "PRIME" ? "Prime Contractor" : "Subcontractor"}</option>)}
            </select></div>
          <div><label className="pg-label">Trade / Specialty</label><input data-testid="contractor-trade-input" className="pg-input" placeholder="e.g. Electrical, HVAC, IT" value={f.trade} onChange={h("trade")} /></div>
          <div className="sm:col-span-2"><label className="pg-label">Certifications</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {CERT_OPTIONS.map((c) => (
                <button type="button" key={c} data-testid={`contractor-cert-${c}`} onClick={() => toggleCert(c)}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-[Poppins] font-semibold border transition-colors ${f.certifications.includes(c) ? "bg-[var(--pg-navy)] text-white border-[var(--pg-navy)]" : "bg-white text-slate-500 border-slate-200 hover:border-[var(--pg-accent)]"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div><label className="pg-label">Contact Name</label><input data-testid="contractor-contact-input" className="pg-input" value={f.contact_name} onChange={h("contact_name")} /></div>
          <div><label className="pg-label">Location</label><input data-testid="contractor-location-input" className="pg-input" placeholder="City, State" value={f.location} onChange={h("location")} /></div>
          <div><label className="pg-label">Email</label><input data-testid="contractor-email-input" type="email" className="pg-input" value={f.email} onChange={h("email")} /></div>
          <div><label className="pg-label">Phone</label><input data-testid="contractor-phone-input" className="pg-input" value={f.phone} onChange={h("phone")} /></div>
          <div className="sm:col-span-2"><label className="pg-label">Website</label><input data-testid="contractor-website-input" type="url" className="pg-input" placeholder="https://" value={f.website} onChange={h("website")} /></div>
          <div className="sm:col-span-2"><label className="pg-label">Notes <span className="text-slate-400 font-normal">(capabilities, past teaming, rates)</span></label>
            <textarea data-testid="contractor-notes-input" rows={3} className="pg-input" value={f.notes} onChange={h("notes")} /></div>
        </div>
        <div className="mt-6 flex gap-3">
          <button data-testid="contractor-form-submit" type="submit" disabled={busy} className="btn-primary flex-1 justify-center disabled:opacity-70">
            {busy ? <Loader2 className="animate-spin" size={17} /> : isEdit ? "Save Changes" : "Add Contractor"}
          </button>
          <button type="button" data-testid="contractor-form-cancel" onClick={onClose} className="px-5 rounded-lg border border-slate-200 text-[13.5px] font-[Poppins] font-semibold text-slate-500 hover:border-slate-400">Cancel</button>
        </div>
      </form>
    </div>
  );
}
